import React, { useMemo, useState } from "react";
import { Button, Input, Select } from "antd";
import { apiOrigin } from "./const";

const { TextArea } = Input;
const { Option } = Select;

const FOOD_API_BASE = `${apiOrigin}/food`;

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatLocalTime = (date) => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const parseDateString = (value) => {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
};

const buildDateRange = (start, end) => {
  const dates = [];
  const startDate = parseDateString(start);
  const endDate = parseDateString(end);
  if (!startDate || !endDate) {
    return dates;
  }
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    dates.push(formatLocalDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(file);
  });

function FoodLogPage() {
  const now = useMemo(() => new Date(), []);
  const [foodDescription, setFoodDescription] = useState("");
  const [mealType, setMealType] = useState("lunch");
  const [dateValue, setDateValue] = useState(formatLocalDate(now));
  const [timeValue, setTimeValue] = useState(formatLocalTime(now));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  const [mealImageFile, setMealImageFile] = useState(null);
  const [mealImagePreview, setMealImagePreview] = useState("");
  const [mealDateValue, setMealDateValue] = useState(formatLocalDate(now));
  const [mealTimeValue, setMealTimeValue] = useState(formatLocalTime(now));
  const [mealTypeValue, setMealTypeValue] = useState("lunch");
  const [isMealSubmitting, setIsMealSubmitting] = useState(false);
  const [mealStatus, setMealStatus] = useState("");

  const today = useMemo(() => formatLocalDate(now), [now]);
  const [rangeStart, setRangeStart] = useState(today);
  const [rangeEnd, setRangeEnd] = useState(today);
  const [logs, setLogs] = useState([]);
  const [logsStatus, setLogsStatus] = useState("");

  const timestamp = useMemo(() => {
    if (!dateValue || !timeValue) {
      return "";
    }
    const date = new Date(`${dateValue}T${timeValue}:00`);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return date.toISOString();
  }, [dateValue, timeValue]);

  const handleImageChange = (event) => {
    const file = event.target.files && event.target.files[0];
    setImageFile(file || null);
    if (!file) {
      setImagePreview("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result || "");
    reader.readAsDataURL(file);
  };

  const handleMealImageChange = (event) => {
    const file = event.target.files && event.target.files[0];
    setMealImageFile(file || null);
    if (!file) {
      setMealImagePreview("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setMealImagePreview(reader.result || "");
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!foodDescription.trim()) {
      setSubmitStatus("请填写食物描述。");
      return;
    }
    if (!timestamp) {
      setSubmitStatus("请选择日期和时间。");
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus("正在提交...");
    try {
      const imageUrl = imageFile ? await fileToDataUrl(imageFile) : "";
      const payload = {
        food_description: foodDescription.trim(),
        timestamp,
        meal_type: mealType,
      };
      if (imageUrl) {
        payload.image_url = imageUrl;
      }

      const response = await fetch(`${FOOD_API_BASE}/log`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.status === "error") {
        throw new Error(data.message || "提交失败");
      }
      setSubmitStatus("已记录食物日志。");
      setFoodDescription("");
      setImageFile(null);
      setImagePreview("");
    } catch (error) {
      setSubmitStatus(error.message || "提交失败");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMealSubmit = async () => {
    if (!mealImageFile) {
      setMealStatus("请上传餐食图片。");
      return;
    }
    setIsMealSubmitting(true);
    setMealStatus("正在提交...");
    try {
      const photoBase64 = await fileToDataUrl(mealImageFile);
      const payload = {
        photo_base64: photoBase64,
        meal_type: mealTypeValue,
      };
      if (mealDateValue && mealTimeValue) {
        const mealTimestamp = new Date(
          `${mealDateValue}T${mealTimeValue}:00`
        ).toISOString();
        payload.timestamp = mealTimestamp;
      }

      const response = await fetch(`${FOOD_API_BASE}/add_meal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.status === "error") {
        throw new Error(data.message || "提交失败");
      }
      setMealStatus("已上传餐食并记录。");
      setMealImageFile(null);
      setMealImagePreview("");
    } catch (error) {
      setMealStatus(error.message || "提交失败");
    } finally {
      setIsMealSubmitting(false);
    }
  };

  const loadLogs = async () => {
    if (!rangeStart || !rangeEnd) {
      setLogsStatus("请选择开始和结束日期。");
      return;
    }
    setLogsStatus("加载中...");
    try {
      const dates = buildDateRange(rangeStart, rangeEnd);
      if (!dates.length) {
        setLogs([]);
        setLogsStatus("日期范围无效。");
        return;
      }
      const responses = await Promise.all(
        dates.map((date) =>
          fetch(`${FOOD_API_BASE}/daily/${date}`).then((res) => res.json())
        )
      );
      const mergedLogs = responses.flatMap((result) =>
        Array.isArray(result?.data) ? result.data : []
      );
      mergedLogs.sort((a, b) => {
        const timeA = new Date(a.timestamp || `${a.date}T00:00:00`).getTime();
        const timeB = new Date(b.timestamp || `${b.date}T00:00:00`).getTime();
        return timeB - timeA;
      });
      setLogs(mergedLogs);
      setLogsStatus(mergedLogs.length ? "" : "暂无记录");
    } catch (error) {
      setLogsStatus(error.message || "加载失败");
    }
  };

  return (
    <div className="food-log-page">
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,500;9..144,700&display=swap");
        .food-log-page {
          --sand: #f7f2ea;
          --clay: #d88f6f;
          --olive: #6a7c59;
          --ink: #2a2b2f;
          --cream: #fffdf9;
          --sky: #f0f4f8;
          font-family: "Space Grotesk", system-ui, -apple-system, sans-serif;
          color: var(--ink);
          max-width: 980px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 24px;
          padding: 8px 4px 32px;
        }
        .food-log-hero {
          padding: 24px 28px;
          border-radius: 20px;
          background: radial-gradient(circle at top left, #fff3e6 0%, #f6efe6 55%, #f0e8dd 100%);
          border: 1px solid #e6d7c8;
          display: flex;
          flex-direction: column;
          gap: 8px;
          animation: fadeIn 0.6s ease;
        }
        .food-log-hero h1 {
          font-family: "Fraunces", serif;
          font-size: 32px;
          margin: 0;
        }
        .food-log-hero p {
          margin: 0;
          color: #63584e;
        }
        .food-log-card {
          padding: 20px;
          border-radius: 16px;
          background: var(--cream);
          border: 1px solid #e3d6c8;
          box-shadow: 0 16px 30px rgba(42, 43, 47, 0.08);
          animation: fadeIn 0.8s ease;
        }
        .food-log-card h2 {
          margin-top: 0;
          font-size: 22px;
        }
        .food-log-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 16px;
          align-items: center;
        }
        .food-log-field label {
          display: block;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 6px;
          color: #6b5d50;
        }
        .food-log-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .food-log-preview {
          width: 140px;
          height: 140px;
          border-radius: 12px;
          border: 1px dashed #c9b8a8;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background: #fff;
          color: #9b8b7c;
        }
        .food-log-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .food-log-status {
          color: #7f6c5d;
        }
        .food-log-table {
          width: 100%;
          border-collapse: collapse;
        }
        .food-log-table th,
        .food-log-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #e0d6cc;
          text-align: left;
          font-size: 14px;
        }
        .food-log-table th {
          background: #f0e9e0;
          font-weight: 600;
        }
        .food-log-empty {
          padding: 12px 0;
          color: #7f6c5d;
        }
        .food-log-button {
          background: var(--olive);
          border-color: var(--olive);
        }
        .food-log-button:hover,
        .food-log-button:focus {
          background: #5a6b4a;
          border-color: #5a6b4a;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div className="food-log-hero">
        <h1>食物记录台</h1>
        <p>用影像与时间刻画你的能量曲线。</p>
      </div>

      <div className="food-log-card">
        <h2>🍱 食物记录</h2>
        <div className="food-log-grid">
          <div className="food-log-field">
            <label>食物描述</label>
            <TextArea
              rows={2}
              value={foodDescription}
              onChange={(event) => setFoodDescription(event.target.value)}
              placeholder="例如: 牛肉面"
            />
          </div>
          <div className="food-log-field">
            <label>餐次</label>
            <Select value={mealType} onChange={setMealType}>
              <Option value="breakfast">breakfast</Option>
              <Option value="lunch">lunch</Option>
              <Option value="dinner">dinner</Option>
              <Option value="snack">snack</Option>
            </Select>
          </div>
          <div className="food-log-field">
            <label>日期</label>
            <input
              type="date"
              value={dateValue}
              onChange={(event) => setDateValue(event.target.value)}
            />
          </div>
          <div className="food-log-field">
            <label>时间</label>
            <input
              type="time"
              value={timeValue}
              onChange={(event) => setTimeValue(event.target.value)}
            />
          </div>
          <div className="food-log-field">
            <label>上传图片</label>
            <div className="food-log-actions">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              <div className="food-log-preview">
                {imagePreview ? (
                  <img src={imagePreview} alt="预览" />
                ) : (
                  <span>暂无图片</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            className="food-log-button"
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            记录食物
          </Button>
          {submitStatus ? (
            <span className="food-log-status" style={{ marginLeft: 12 }}>
              {submitStatus}
            </span>
          ) : null}
        </div>
      </div>

      <div className="food-log-card">
        <h2>📷 上传餐食（识别多个食物）</h2>
        <div className="food-log-grid">
          <div className="food-log-field">
            <label>餐次</label>
            <Select value={mealTypeValue} onChange={setMealTypeValue}>
              <Option value="breakfast">breakfast</Option>
              <Option value="lunch">lunch</Option>
              <Option value="dinner">dinner</Option>
              <Option value="snack">snack</Option>
            </Select>
          </div>
          <div className="food-log-field">
            <label>日期</label>
            <input
              type="date"
              value={mealDateValue}
              onChange={(event) => setMealDateValue(event.target.value)}
            />
          </div>
          <div className="food-log-field">
            <label>时间</label>
            <input
              type="time"
              value={mealTimeValue}
              onChange={(event) => setMealTimeValue(event.target.value)}
            />
          </div>
          <div className="food-log-field">
            <label>上传图片</label>
            <div className="food-log-actions">
              <input
                type="file"
                accept="image/*"
                onChange={handleMealImageChange}
              />
              <div className="food-log-preview">
                {mealImagePreview ? (
                  <img src={mealImagePreview} alt="餐食预览" />
                ) : (
                  <span>暂无图片</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            className="food-log-button"
            onClick={handleMealSubmit}
            loading={isMealSubmitting}
          >
            上传餐食
          </Button>
          {mealStatus ? (
            <span className="food-log-status" style={{ marginLeft: 12 }}>
              {mealStatus}
            </span>
          ) : null}
        </div>
      </div>

      <div className="food-log-card">
        <h2>历史记录</h2>
        <div className="food-log-grid" style={{ alignItems: "end" }}>
          <div className="food-log-field">
            <label>开始日期</label>
            <input
              type="date"
              value={rangeStart}
              onChange={(event) => setRangeStart(event.target.value)}
            />
          </div>
          <div className="food-log-field">
            <label>结束日期</label>
            <input
              type="date"
              value={rangeEnd}
              onChange={(event) => setRangeEnd(event.target.value)}
            />
          </div>
          <div className="food-log-field">
            <Button onClick={loadLogs}>查看记录</Button>
          </div>
        </div>
        {logs.length ? (
          <table className="food-log-table">
            <thead>
              <tr>
                <th>日期</th>
                <th>时间</th>
                <th>食物</th>
                <th>餐次</th>
                <th>卡路里</th>
                <th>蛋白质</th>
                <th>脂肪</th>
                <th>碳水</th>
                <th>份量(g)</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((item) => (
                <tr key={`${item.id ?? item.timestamp}-${item.food_name}`}>
                  <td>{item.date}</td>
                  <td>{item.timestamp ? item.timestamp.slice(11, 16) : ""}</td>
                  <td>{item.food_name || item.food_description || ""}</td>
                  <td>{item.meal_type || ""}</td>
                  <td>{item.cal ?? item.calories ?? ""}</td>
                  <td>{item.protein ?? ""}</td>
                  <td>{item.fat ?? ""}</td>
                  <td>{item.carbohydrates ?? ""}</td>
                  <td>{item.weight ?? item.serving_size ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="food-log-empty">{logsStatus || "暂无记录"}</div>
        )}
      </div>
    </div>
  );
}

export default FoodLogPage;
